import { PayloadAction } from '@reduxjs/toolkit';
import { ApiResponse } from 'apisauce';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, Observer } from 'rxjs';
import { debounceTime, filter, mergeMap, switchMap } from 'rxjs/operators';

import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { CharacterEnum } from '../../enums/character.enum';
import { PagedResponse, SimplePagedResponse } from '../../models/common-response-types';
import { Company } from '../../models/company';
import {
  BaseRatiosParamType,
  BaseStatementSearchParamType,
  CompanyLogoAndNameResponse,
  EpicMoversParamType,
  EpicMoversResponse,
  ETF,
  ETFDetailParams,
  ETFDetailResponse,
  ETFParams,
  GapperParamType,
  GapperResponse,
  MarketCountryCode,
  NewStockData,
  NonWorkingDaysByCountryResponse,
  PageableFilterRequest,
  PopularStock,
  PopularStockParams,
  SectorDetail,
  Earning,
  SectorDetailParams,
  StocksSymbolSet,
  SymbolAndOptionalExchangeCode,
  TopGainerAndLoserData,
  Week52HighLowParamType,
  Week52HighLowStatusType,
  Week52HighLowTopListParamType,
  Week52HighLowTopListResponse,
  MarketCapCategory,
  MarketCapData,
  MarketCapsParamType,
  MarketCapsRequest,
} from '../../models/fundamental/types';
import { DEFAULT_PAGE_RESPONSE } from '../../models/pageable';
import { DynamicFilterBuilder } from '../../services/filter/dynamic-filter-builder';
import GlobalService from '../../services/Global.service';
import marketHoursState from '../../store-util/MarketHoursState';
import { isCallStatusReady } from '../../util/error-handling/StatusByCallHelpers';
import { isEmpty } from '../../util/typeGuards';
import { servicesReady } from '../common-actions';
import { processErrorInResponse, processResponse } from '../helpers';
import { addCompanyToCache, retrieveFavoriteStocksFromServerCompleted } from '../market-data';
import {
  getAccountPendingOrdersSuccess,
  getAccountTradeHistorySuccess,
  getOpenPositionsSuccess,
  getPeopleAlsoTradeDataCompleted,
} from '../reporting';
import {
  AccountTradeHistoryResponseData,
  OpenPosition,
  TradeHistoryParsedOrders,
} from '../reporting/types';
import { CachedResponse } from '../types';
import { RootState } from '..';

import {
  getCompanyLogoAndNameSymbolBatches,
  mergeCompanyLogoAndNameResponses,
} from './companyLogoAndName.helpers';
import {
  DEFAULT_NON_WORKING_DAYS_RESPONSE,
  DEFAULT_PAGE,
  DEFAULT_PAGE_LIST_SIZE,
  etfDetailProjections,
  etfProjections,
  WEEK_52_HIGH_LOW_TOP_LIST_COUNT,
  MARKET_CAPS_GAINERS_SORT,
  MARKET_CAPS_LOSERS_SORT,
  MARKET_CAPS_PAGE_SIZE,
  PREVIOUS_EARNINGS_COUNT,
  marketCapsProjections,
} from './constants';
import {
  buildPreviousEarningsSearchPayload,
  clearCallCache,
  getPreviousEarningsCacheKey,
  getPriceForTopMovers,
  getRatiosSearchParamsByPayload,
  getStatementSearchParamsByPayload,
  isEarningReported,
  populateEarningCalendarLabels,
  processCallCache,
} from './helpers';
import {
  getAllRatios,
  getAllRatiosCompleted,
  getAllRatiosFailed,
  getAvailableIndexes,
  getAvailableIndexesCompleted,
  getAvailableIndexesFailed,
  getCompany,
  getCompanyCompleted,
  getCompanyFailed,
  getCompanyLogoAndName,
  getCompanyLogoAndNameCompleted,
  getCompanyLogoAndNameFailed,
  getDividendPayers,
  getDividendPayersCompleted,
  getDividendPayersFailed,
  getEpicMovers,
  getEpicMoversCompleted,
  getEpicMoversFailed,
  getETFDetails,
  getETFDetailsCompleted,
  getETFDetailsFailed,
  getETFs,
  getETFsCompleted,
  getETFsFailed,
  getGappers,
  getGappersCompleted,
  getGappersFailed,
  getHighGrowthCompanies,
  getHighGrowthCompaniesCompleted,
  getHighGrowthCompaniesFailed,
  getMarketIndexes,
  getMarketIndexesCompleted,
  getMarketIndexesFailed,
  getNewStocks,
  getNewStocksCompleted,
  getNewStocksFailed,
  getNonWorkingDaysAndMarketTime,
  getNonWorkingDaysAndMarketTimeCompleted,
  getNonWorkingDaysAndMarketTimeFailed,
  getPopularStocks,
  getPopularStocksCompleted,
  getPopularStocksFailed,
  getRatiosLiquidityAndSolvency,
  getRatiosLiquidityAndSolvencyCompleted,
  getRatiosLiquidityAndSolvencyFailed,
  getRatiosProfitability,
  getRatiosProfitabilityCompleted,
  getRatiosProfitabilityFailed,
  getRatiosValuation,
  getRatiosValuationCompleted,
  getRatiosValuationFailed,
  getSectorDetail,
  getSectorDetailCompleted,
  getSectorDetailFailed,
  getSectors,
  getSectorsCompleted,
  getSectorsFailed,
  getStatements,
  getStatementsBalanceSheet,
  getStatementsBalanceSheetCompleted,
  getStatementsBalanceSheetFailed,
  getStatementsCashFlow,
  getStatementsCashFlowCompleted,
  getStatementsCashFlowFailed,
  getStatementsCompleted,
  getStatementsFailed,
  getStatementsIncome,
  getStatementsIncomeCompleted,
  getStatementsIncomeFailed,
  getWeek52HighLow,
  getWeek52HighLowCompleted,
  getWeek52HighLowFailed,
  getWeek52HighLowTopList,
  getWeek52HighLowTopListCompleted,
  getWeek52HighLowTopListFailed,
  keyStatsCompleted,
  keyStatsFailed,
  loadStock,
  searchEarnings,
  searchEarningsCompleted,
  searchEarningsFailed,
  getPreviousEarnings,
  getPreviousEarningsCompleted,
  getPreviousEarningsFailed,
  searchStock,
  searchStockByParams,
  stockSearchCompleted,
  stockSearchFailed,
  stockSearchReset,
  getMarketCaps,
  getMarketCapsCompleted,
  getMarketCapsFailed,
} from './index';
import {
  FundamentalCall,
  GetPreviousEarningsPayload,
  SearchEarningsPayload,
  SearchStockPayload,
} from './types';

type FundamentalEpicState = RootState;

const searchStockResetEpic = (action$: Observable<any>, state$: StateObservable<FundamentalEpicState>) => action$.pipe(
  ofType(searchStock.type),
  filter((action: PayloadAction<string>) => (
    state$.value.fundamental.stockResults?.length > 0
    || action.payload === ''
  )),
  mergeMap((action: PayloadAction<string>) => {
    const isEmptySearch = action.payload === '';
    return new Observable((observer: Observer<any>) => observer.next(stockSearchReset(isEmptySearch)));
  }),
);

const searchStocksEpic = (action$: Observable<any>, state$: StateObservable<FundamentalEpicState>) => action$.pipe(
  ofType(searchStock.type),
  debounceTime(500),
  filter(action => action.payload !== ''),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    GlobalService
      .fundamentalService
      .searchCompanies(action.payload, undefined)
      .then(response => {
        processResponse(searchStock.type, {}, response, observer, stockSearchCompleted, stockSearchFailed);
      })
      .catch(error => {
        processErrorInResponse(searchStock.type, error, action, observer, stockSearchFailed);
      });
  })),
);

const searchStockByParamsEpic = (
  action$: Observable<any>,
  state$: StateObservable<FundamentalEpicState>,
) => action$.pipe(
  ofType(searchStockByParams.type),
  debounceTime(PredefinedMilisecondsTypeEnum.halfSecond), // In order to not make HTTP requests for each key press
  filter((action: PayloadAction<SearchStockPayload>) => action.payload.query !== ''),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const { query, limit, params } = action.payload;
    GlobalService
      .fundamentalService
      .searchCompanies(query, params, limit)
      .then(response => {
        processResponse(
          searchStockByParams.type,
          {},
          response,
          observer,
          stockSearchCompleted,
          stockSearchFailed,
        );
      })
      .catch(error => {
        processErrorInResponse(searchStockByParams.type, error, action, observer, stockSearchFailed);
      });
  })),
);


const getPreviousEarningsEpic = (action$: Observable<any>) => action$.pipe(
  ofType(getPreviousEarnings.type),
  filter((action: PayloadAction<GetPreviousEarningsPayload>) => (
    processCallCache(getPreviousEarningsCacheKey(action.payload), 'getPreviousEarnings') !== ''
  )),
  mergeMap((action: PayloadAction<GetPreviousEarningsPayload>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getPreviousEarningsEpic';
    const key = processCallCache(getPreviousEarningsCacheKey(action.payload), 'getPreviousEarnings', true);

    if (!key) return;

    GlobalService.fundamentalService
      .searchEarnings(buildPreviousEarningsSearchPayload(action.payload))
      .then(response => {
        if (!response.ok || !response.data) {
          clearCallCache(key, 'getPreviousEarnings');
          processResponse(callName, {}, response, observer, getPreviousEarningsCompleted, getPreviousEarningsFailed);
          return;
        }

        processCallCache(key, 'getPreviousEarnings', false);
        const populated = populateEarningCalendarLabels(response.data);
        // The backend can't filter by report status - keep reported rows only, capped for display.
        const content: Earning[] = (populated?.content ?? [])
          .filter(isEarningReported)
          .slice(0, PREVIOUS_EARNINGS_COUNT);
        processResponse(
          callName,
          {},
          response,
          observer,
          getPreviousEarningsCompleted,
          getPreviousEarningsFailed,
          false,
          null,
          { key, content },
        );
      })
      .catch(error => {
        clearCallCache(key, 'getPreviousEarnings');
        processErrorInResponse(callName, error, action, observer, getPreviousEarningsFailed);
      });
  })),
);

const searchEarningsEpic = (action$: Observable<any>) => action$.pipe(
  ofType(searchEarnings.type),
  mergeMap((action: PayloadAction<SearchEarningsPayload>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/searchEarningsEpic';
    GlobalService
      .fundamentalService
      .searchEarnings(action.payload)
      .then(response => {
        const success = processResponse(
          callName,
          {},
          response,
          observer,
          searchEarningsCompleted,
          searchEarningsFailed,
        );
        if (success && response.data) {
          const symbols: string[] = [];
          const { content } = response.data;

          content.forEach(el => { symbols.push(el.symbol); });
          observer.next(getCompanyLogoAndName(symbols.join()));
        }
      }).catch(error => processErrorInResponse(callName, error, action, observer, searchEarningsFailed));
  })),
);

const searchKeyStatsEpic = (action$: Observable<any>, state$: StateObservable<FundamentalEpicState>) => action$.pipe(
  ofType(loadStock.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/searchKeyStatsEpic';

    GlobalService
      .fundamentalService
      .getKeyStats(action.payload.symbol)
      .then(response => processResponse(
        callName,
        {},
        response,
        observer,
        keyStatsCompleted,
        keyStatsFailed,
      ))
      .catch(error => processErrorInResponse(callName, error, action, observer, keyStatsFailed));
  })),
);


const isFailedLogoAndNameResponse = (response: ApiResponse<CompanyLogoAndNameResponse[]>) => (
  !response
  || response.problem
  || (!!response.status && response.status > 299)
);

const handleLogoAndNameBatchResponses = (
  responses: ApiResponse<CompanyLogoAndNameResponse[]>[],
  symbolBatches: string[],
  observer: Observer<any>,
  callName: string,
) => {
  const batchResponses = responses.map((response, index) => ({
    response,
    symbolBatch: symbolBatches[index],
  }));

  const failedBatchResponses = batchResponses.filter(({ response }) => isFailedLogoAndNameResponse(response));
  const successfulBatchResponses = batchResponses.filter(({ response }) => !isFailedLogoAndNameResponse(response));

  if (successfulBatchResponses.length === 0) {
    failedBatchResponses.forEach(({ response, symbolBatch }) => {
      clearCallCache(symbolBatch, 'getCompanyLogoAndName');
      processResponse(callName, {}, response, observer, getCompanyLogoAndNameCompleted, getCompanyLogoAndNameFailed);
    });
    return;
  }

  processCallCache(
    successfulBatchResponses.map(({ symbolBatch }) => symbolBatch).join(CharacterEnum.Comma),
    'getCompanyLogoAndName',
    false,
  );

  const responseData = mergeCompanyLogoAndNameResponses(
    successfulBatchResponses.map(({ response }) => response.data ?? []),
  );
  const mergedResponse = {
    ...successfulBatchResponses[0].response,
    data: responseData,
    ok: true,
    problem: null,
    status: successfulBatchResponses[0].response?.status,
  };

  const success = processResponse(
    callName,
    {},
    mergedResponse,
    observer,
    getCompanyLogoAndNameCompleted,
    getCompanyLogoAndNameFailed,
  );

  if (success && responseData.length) {
    observer.next(addCompanyToCache({
      responseData,
      symbols: successfulBatchResponses.map(({ symbolBatch }) => symbolBatch).join(CharacterEnum.Comma),
    }));
  }

  failedBatchResponses.forEach(({ response, symbolBatch }) => {
    clearCallCache(symbolBatch, 'getCompanyLogoAndName');
    processResponse(callName, {}, response, observer, getCompanyLogoAndNameCompleted, getCompanyLogoAndNameFailed);
  });
};

const getCompanyLogoAndNameEpic = (action$: Observable<any>) => action$.pipe(
  ofType(getCompanyLogoAndName.type),
  filter(action => processCallCache(getSymbols(action), 'getCompanyLogoAndName') !== ''),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getCompanyLogoAndName';
    const symbols = processCallCache(getSymbols(action), 'getCompanyLogoAndName', true);
    const symbolBatches = getCompanyLogoAndNameSymbolBatches(symbols);

    if (isEmpty(symbols) || isEmpty(symbolBatches)) {
      return;
    }

    Promise
      .all(symbolBatches.map(symbolBatch => GlobalService.fundamentalService.getCompanyLogoAndName(symbolBatch)))
      .then(responses => handleLogoAndNameBatchResponses(responses, symbolBatches, observer, callName))
      .catch(error => {
        clearCallCache(symbols, 'getCompanyLogoAndName');
        processErrorInResponse(callName, error, action, observer, getCompanyLogoAndNameFailed);
      });
  })),
);

const triggerGetCompanyLogoAndNameEpic = (
  action$: Observable<any>,
  state$: StateObservable<FundamentalEpicState>,
) => action$.pipe(
  ofType(
    // file server
    retrieveFavoriteStocksFromServerCompleted.type,

    // reporting
    getOpenPositionsSuccess.type,
    getAccountTradeHistorySuccess.type,
    getAccountPendingOrdersSuccess.type,
    getPeopleAlsoTradeDataCompleted.type,

    // fundamental
    getETFsCompleted.type,
    getGappersCompleted.type,
    getWeek52HighLowTopListCompleted.type,
    getWeek52HighLowCompleted.type,
    getSectorDetailCompleted.type,
    getEpicMoversCompleted.type,
    getNewStocksCompleted.type,
    getPopularStocksCompleted.type,
    getCompanyCompleted.type,
  ),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const { payload } = action;
    let mappedSymbols: string[] = [];

    switch (action.type) {
      case retrieveFavoriteStocksFromServerCompleted.type: {
        const { userId } = payload.customData;
        const favouriteStock = state$.value.marketData.favouriteStocks[userId];
        mappedSymbols = favouriteStock.map(item => item.symbol);
        break;
      }

      case getOpenPositionsSuccess.type: {
        const { openPositions } = state$.value.reporting ?? {};
        mappedSymbols = openPositions?.map((item: OpenPosition) => item.symbol);
        break;
      }

      case getAccountTradeHistorySuccess.type: {
        const { tradeHistoryOrders } = state$.value.reporting ?? {};
        mappedSymbols = tradeHistoryOrders?.map((item: TradeHistoryParsedOrders) => item.symbol);
        break;
      }

      case getAccountPendingOrdersSuccess.type: {
        const { pendingOrders } = state$.value.reporting ?? {};
        mappedSymbols = pendingOrders?.map((item: AccountTradeHistoryResponseData) => item.symbol);
        break;
      }

      case getPopularStocksCompleted.type:
      case getPeopleAlsoTradeDataCompleted.type: {
        const payloadAction = (action.payload ?? []) as Array<PopularStock | { symbol: string }>;
        mappedSymbols = payloadAction.map(item => item.symbol);
        break;
      }

      case getEpicMoversCompleted.type: {
        const topGainers = payload?.topGainers ?? [];
        const topLosers = payload?.topLosers ?? [];
        mappedSymbols = topGainers.concat(...topLosers).map((item: TopGainerAndLoserData) => item.symbol);
        break;
      }

      case getWeek52HighLowTopListCompleted.type: {
        const topNHigh = payload?.topNHigh ?? [];
        const topNLow = payload?.topNLow ?? [];
        mappedSymbols = topNHigh.concat(...topNLow).map((item: SymbolAndOptionalExchangeCode) => item.symbol);
        break;
      }

      case getSectorDetailCompleted.type:
      case getWeek52HighLowCompleted.type:
      case getGappersCompleted.type:
      case getETFsCompleted.type: {
        const symbols = payload?.content ?? [];
        mappedSymbols = symbols.map((item: SymbolAndOptionalExchangeCode) => item.symbol);
        break;
      }

      case getCompanyCompleted.type: {
        const companyArray = payload ?? [] as Company[];
        mappedSymbols = companyArray.map((item: Company) => item.symbol);
        break;
      }

      default:
        // Use the default mappedSymbols
        break;
    }

    if (isEmpty(mappedSymbols)) {
      return;
    }

    // get company name,logo, stockexchange code
    observer.next(getCompanyLogoAndName(mappedSymbols.join()));
  })),
);

// TODO: Delete after merging ART-1918 and use getCompanyLogoAndName for both mobile and web
const getCompanyEpic = (action$: Observable<any>, state$: StateObservable<FundamentalEpicState>) => action$.pipe(
  ofType(getCompany.type, loadStock.type),
  filter(action => (
    !state$.value.fundamental.companies.length
    || !state$.value.fundamental.companies.find(el => el.symbol === getSymbols(action)))),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getCompany';
    const symbols: string = getSymbols(action);

    GlobalService
      .fundamentalService
      .getCompany(symbols)
      .then((response: ApiResponse<any>) => {
        if ((response as any) as CachedResponse === 'cached') return;

        const isSuccess = processResponse(callName, symbols, response, observer, getCompanyCompleted, getCompanyFailed);
        if (isSuccess) {
          observer.next(addCompanyToCache({ responseData: response.data, symbols }));
        }
      })
      .catch(error => processErrorInResponse(callName, error, action, observer, getCompanyFailed));
  })),
);

const getPopularStocksEpic = (
  action$: Observable<any>,
  state$: StateObservable<FundamentalEpicState>,
) => action$.pipe(
  ofType(getPopularStocks.type),
  mergeMap((action: PayloadAction<PopularStockParams>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getPopularStocks';
    const { constrained, count } = action.payload ?? {};
    GlobalService
      .fundamentalService
      .getPopularStocks(constrained, count)
      .then((response: ApiResponse<PopularStock[]>) => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getPopularStocksCompleted,
          getPopularStocksFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getPopularStocksFailed,
      ));
  })),
);

type RangeAction = PayloadAction<{
  startDate?: string,
  endDate?: string,
}>;


type SplitsAction = PayloadAction<{
  symbols: string[],
  startDate?: string,
  endDate?: string,
}>;


const getNewStocksEpic = (action$: Observable<any>, state$: StateObservable<FundamentalEpicState>) => action$.pipe(
  ofType(getNewStocks.type),
  filter(() => state$.value.app.isServicesReady),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getNewStocks';
    const params = action.payload;

    GlobalService
      .fundamentalService
      .getNewStocks(params)
      .then((response: ApiResponse<NewStockData[]>) => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getNewStocksCompleted,
          getNewStocksFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getNewStocksFailed,
      ));
  })),
);

const getDividendPayersEpic = (
  action$: Observable<any>,
  state$: StateObservable<FundamentalEpicState>,
) => action$.pipe(
  ofType(getDividendPayers.type),
  filter(() => state$.value.app.isServicesReady),
  mergeMap((action: PayloadAction<PageableFilterRequest>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getDividendPayers';
    GlobalService
      .fundamentalService
      .getDividendPayers(action?.payload)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getDividendPayersCompleted,
          getDividendPayersFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getDividendPayersFailed,
      ));
  })),
);

const getHighGrowthCompaniesEpic = (
  action$: Observable<any>,
  state$: StateObservable<FundamentalEpicState>,
) => action$.pipe(
  ofType(getHighGrowthCompanies.type),
  filter(() => state$.value.app.isServicesReady),
  mergeMap((action: PayloadAction<PageableFilterRequest>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getHighGrowthCompanies';
    GlobalService
      .fundamentalService
      .getHighGrowthCompanies(action?.payload)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getHighGrowthCompaniesCompleted,
          getHighGrowthCompaniesFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getHighGrowthCompaniesFailed,
      ));
  })),
);

/**
 * This Epic depends on getAvailableIndexesEpic.
 * First, we need to load the available list and then we can use the symbol list from the response to call this epic
 */
const getMarketIndexesEpic = (
  action$: Observable<any>,
  state$: StateObservable<FundamentalEpicState>,
) => action$.pipe(
  ofType(getMarketIndexes.type, getAvailableIndexesCompleted.type),
  filter(() => isCallStatusReady(FundamentalCall.getAvailableIndexes, state$.value.fundamental.statusByCall)), // To be sure the available list is loaded
  filter(() => !isEmpty(state$.value.fundamental.availableIndexes)),
  mergeMap((action: PayloadAction) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getMarketIndexes';
    const symbols = state$.value.fundamental.availableIndexes.map(item => item.symbol);
    GlobalService
      .fundamentalService
      .getMarketIndexes(symbols)
      .then(response => processResponse(
        callName,
        {},
        response,
        observer,
        getMarketIndexesCompleted,
        getMarketIndexesFailed,
      ))
      .catch(error => processErrorInResponse(callName, error, action, observer, getMarketIndexesFailed));
  })),
);

const toMarketCapsPage = (
  response: ApiResponse<SimplePagedResponse<MarketCapData>>,
  isInList: (changesPercentage: number) => boolean,
): SimplePagedResponse<MarketCapData> => {
  const page = response.data ?? { ...DEFAULT_PAGE_RESPONSE };
  const content = (page.content ?? []).filter(item => isInList(item.changesPercentage ?? 0));

  return { ...page, content, numberOfElements: content.length };
};

const buildMarketCapsParams = (category: MarketCapCategory, page: number, sort: string): MarketCapsRequest => ({
  page,
  size: MARKET_CAPS_PAGE_SIZE,
  sort,
  projections: marketCapsProjections,
  filters: new DynamicFilterBuilder().equal('category', category).build(),
  symbolSet: StocksSymbolSet.ACE,
});

/**
 * The endpoint serves one sorted page at a time, so the gainers and losers of a market cap
 * bucket are two calls sorted in opposite directions. They complete as one action - the widget
 * shows both lists side by side and a half-filled state would flash an empty column.
 */
const getMarketCapsEpic = (
  action$: Observable<PayloadAction<MarketCapsParamType>>,
) => action$.pipe(
  ofType(getMarketCaps.type),
  switchMap((action: PayloadAction<MarketCapsParamType>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/symbols/market-caps';
    const { category, page = DEFAULT_PAGE } = action.payload;

    Promise
      .all([
        GlobalService.fundamentalService.getMarketCaps(buildMarketCapsParams(category, page, MARKET_CAPS_GAINERS_SORT)),
        GlobalService.fundamentalService.getMarketCaps(buildMarketCapsParams(category, page, MARKET_CAPS_LOSERS_SORT)),
      ])
      .then(([gainersResponse, losersResponse]) => {
        const failedResponse = [gainersResponse, losersResponse].find(response => !response.ok);

        if (failedResponse) {
          processResponse(
            callName,
            {},
            failedResponse,
            observer,
            getMarketCapsCompleted,
            getMarketCapsFailed,
          );
          return;
        }

        observer.next(getMarketCapsCompleted({
          gainers: toMarketCapsPage(gainersResponse, changesPercentage => changesPercentage > 0),
          losers: toMarketCapsPage(losersResponse, changesPercentage => changesPercentage < 0),
        }));
      })
      .catch(error => processErrorInResponse(callName, error, action, observer, getMarketCapsFailed));
  })),
);

const getEpicMoversEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getEpicMovers.type),
  mergeMap((action: PayloadAction<EpicMoversParamType>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/symbols/top-gainers-losers';
    const params: EpicMoversParamType = { ...action.payload, symbolSet: StocksSymbolSet.ACE };
    GlobalService
      .fundamentalService
      .getEpicMovers(params)
      .then((response: ApiResponse<EpicMoversResponse>) => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getEpicMoversCompleted,
          getEpicMoversFailed,
          false,
          null,
          null,
          null,
          null,
          { 404: [] },
          (data: EpicMoversResponse) => {
            getPriceForTopMovers(data.topGainers, 'TopGainers');
            getPriceForTopMovers(data.topLosers, 'TopLosers');
          },
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getEpicMoversFailed,
      ));
  })),
);

const getSectorsEpic = (action$: Observable<any>, state$: StateObservable<FundamentalEpicState>) => action$.pipe(
  ofType(getSectors.type),
  filter(() => state$.value.app.isServicesReady),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getSectors';
    GlobalService
      .fundamentalService
      .getSectors()
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getSectorsCompleted,
          getSectorsFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getSectorsFailed,
      ));
  })),
);

const getSectorDetailEpic = (action$: Observable<any>, state$: StateObservable<FundamentalEpicState>) => action$.pipe(
  ofType(getSectorDetail.type),
  filter(() => state$.value.app.isServicesReady),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getSectorDetail';

    const sectorId = action.payload;

    let params: SectorDetailParams = {
      sectorId,
      page: 0,
      size: DEFAULT_PAGE_LIST_SIZE,
    };

    if (state$.value.fundamental.sectorDetails.sectorId === sectorId) {
      params = { ...params, page: state$.value.fundamental.sectorDetails.nextPageToRead };
    }

    GlobalService
      .fundamentalService
      .getSectorDetail(params)
      .then((response: ApiResponse<SectorDetail>) => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getSectorDetailCompleted,
          getSectorDetailFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getSectorDetailFailed,
      ));
  })),
);

const getETFsEpic = (
  action$: Observable<any>,
  state$: StateObservable<FundamentalEpicState>,
) => action$.pipe(
  ofType(getETFs.type),
  mergeMap((action: PayloadAction<PagedResponse<ETF>>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getETFs';

    const params: ETFParams = {
      filters: new DynamicFilterBuilder().equal('type', 'et').build(),
      projections: etfProjections,
      symbolSet: StocksSymbolSet.ACE,
      ...action.payload,
    };

    GlobalService
      .fundamentalService
      .getETFs(params)
      .then(response => {
        const success = processResponse(
          callName,
          {},
          response,
          observer,
          getETFsCompleted,
          getETFsFailed,
        );

        if (success && response.data) {
          const content = response.data?.content ?? [];
          const symbols = content.map(item => item.symbol);
          observer.next(getETFDetails({ filters: new DynamicFilterBuilder().in('symbol', symbols.join(CharacterEnum.Comma)).build() }));
        }
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getETFsFailed,
      ));
  })),
);

const getETFDetailsEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getETFDetails.type),
  mergeMap((action: PayloadAction<PagedResponse<ETFDetailResponse>>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getETFDetails';
    const params: ETFDetailParams = {
      ...action.payload,
      page: DEFAULT_PAGE,
      projections: etfDetailProjections,
      size: DEFAULT_PAGE_LIST_SIZE,
    };

    GlobalService
      .fundamentalService
      .getETFDetails(params)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getETFDetailsCompleted,
          getETFDetailsFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getETFDetailsFailed,
      ));
  })),
);




const getStatementsEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getStatements.type),
  mergeMap((action: PayloadAction<BaseStatementSearchParamType>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/statements';
    const params = getStatementSearchParamsByPayload(action.payload);

    GlobalService
      .fundamentalService
      .getStatements(params)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getStatementsCompleted,
          getStatementsFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getStatementsFailed,
      ));
  })),
);

const getStatementsCashFlowEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getStatementsCashFlow.type),
  mergeMap((action: PayloadAction<BaseStatementSearchParamType>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/statements/cash_flow';
    const params = getStatementSearchParamsByPayload(action.payload);

    GlobalService
      .fundamentalService
      .getStatementsCashFlow(params)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getStatementsCashFlowCompleted,
          getStatementsCashFlowFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getStatementsCashFlowFailed,
      ));
  })),
);

const getStatementsBalanceSheetEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getStatementsBalanceSheet.type),
  mergeMap((action: PayloadAction<BaseStatementSearchParamType>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/statements/balance-sheet';
    const params = getStatementSearchParamsByPayload(action.payload);

    GlobalService
      .fundamentalService
      .getStatementsBalanceSheet(params)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getStatementsBalanceSheetCompleted,
          getStatementsBalanceSheetFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getStatementsBalanceSheetFailed,
      ));
  })),
);

const getStatementsIncomeEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getStatementsIncome.type),
  mergeMap((action: PayloadAction<BaseStatementSearchParamType>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/statements/income';
    const params = getStatementSearchParamsByPayload(action.payload);

    GlobalService
      .fundamentalService
      .getStatementsIncome(params)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getStatementsIncomeCompleted,
          getStatementsIncomeFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getStatementsIncomeFailed,
      ));
  })),
);

const getWeek52HighLowEpic = (
  action$: Observable<any>,
  state$: StateObservable<FundamentalEpicState>,
) => action$.pipe(
  ofType(getWeek52HighLow.type),
  mergeMap((action: PayloadAction<Week52HighLowParamType>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/symbols/week52-high-lows';
    const { payload } = action;
    const sort = payload.changeStatus === Week52HighLowStatusType.Gainers ? 'changePercent,desc' : 'changePercent,asc';
    const params: Week52HighLowParamType = { ...payload, symbolSet: StocksSymbolSet.ACE, sort };

    GlobalService
      .fundamentalService
      .getWeek52HighLow(params)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getWeek52HighLowCompleted,
          getWeek52HighLowFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getWeek52HighLowFailed,
      ));
  })),
);

const getWeek52HighLowTopListEpic = (
  action$: Observable<any>,
  state$: StateObservable<FundamentalEpicState>,
) => action$.pipe(
  ofType(getWeek52HighLowTopList.type),
  mergeMap((action: PayloadAction<void>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/symbols/week52-top-n-high-lows';

    const params: Week52HighLowTopListParamType = {
      topN: WEEK_52_HIGH_LOW_TOP_LIST_COUNT,
      symbolSet: StocksSymbolSet.ACE,
    };

    GlobalService
      .fundamentalService
      .getWeek52HighLowTopList(params)
      .then((response: ApiResponse<Week52HighLowTopListResponse>) => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getWeek52HighLowTopListCompleted,
          getWeek52HighLowTopListFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getWeek52HighLowTopListFailed,
      ));
  })),
);

const getAllRatiosEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getAllRatios.type),
  mergeMap((action: PayloadAction<BaseRatiosParamType>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/ratios/by-type';
    const params = getRatiosSearchParamsByPayload(action.payload);

    GlobalService
      .fundamentalService
      .getAllRatios(params)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getAllRatiosCompleted,
          getAllRatiosFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getAllRatiosFailed,
      ));
  })),
);

const getRatiosValuationEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getRatiosValuation.type),
  mergeMap((action: PayloadAction<BaseRatiosParamType>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/ratios/valuation';
    const params = getRatiosSearchParamsByPayload(action.payload);

    GlobalService
      .fundamentalService
      .getRatiosValuation(params)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getRatiosValuationCompleted,
          getRatiosValuationFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getRatiosValuationFailed,
      ));
  })),
);

const getRatiosProfitabilityEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getRatiosProfitability.type),
  mergeMap((action: PayloadAction<BaseRatiosParamType>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/ratios/by-type/profitability';
    const params = getRatiosSearchParamsByPayload(action.payload);

    GlobalService
      .fundamentalService
      .getRatiosProfitability(params)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getRatiosProfitabilityCompleted,
          getRatiosProfitabilityFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getRatiosProfitabilityFailed,
      ));
  })),
);

const getRatiosLiquidityAndSolvencyEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getRatiosLiquidityAndSolvency.type),
  mergeMap((action: PayloadAction<BaseRatiosParamType>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/ratios/by-type/liquidity-and-solvency';
    const params = getRatiosSearchParamsByPayload(action.payload);

    GlobalService
      .fundamentalService
      .getRatiosLiquidityAndSolvency(params)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getRatiosLiquidityAndSolvencyCompleted,
          getRatiosLiquidityAndSolvencyFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getRatiosLiquidityAndSolvencyFailed,
      ));
  })),
);

const getGappersEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getGappers.type),
  mergeMap((action: PayloadAction<GapperParamType>) => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/symbols/gappers';
    const params: GapperParamType = { ...action.payload, symbolSet: StocksSymbolSet.ACE };

    GlobalService
      .fundamentalService
      .getGappers(params)
      .then((response: ApiResponse<SimplePagedResponse<GapperResponse>>) => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getGappersCompleted,
          getGappersFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getGappersFailed,
      ));
  })),
);



/**
 * After loading the available list, we need to load indexes.
 * First, this epic will be called after this getMarketIndexesEpic should be called
 */
const getAvailableIndexesEpic = (
  action$: Observable<any>,
  state$: StateObservable<FundamentalEpicState>,
) => action$.pipe(
  ofType(getAvailableIndexes.type, getMarketIndexes.type),
  filter(() => isEmpty(state$.value.fundamental.availableIndexes)),
  mergeMap((action: PayloadAction) => new Observable((observer: Observer<any>) => {
    GlobalService
      .fundamentalService
      .getAvailableIndexes()
      .then(response => {
        processResponse(
          'fundamental/symbols/available-indexes',
          {},
          response,
          observer,
          getAvailableIndexesCompleted,
          getAvailableIndexesFailed,
        );
      });
  })),
);

const getNonWorkingDaysAndMarketTimeEpic = (action$: Observable<any>) => action$.pipe(
  ofType(getNonWorkingDaysAndMarketTime.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getNonWorkingDaysAndMarketTimeEpic';
    const countryCode = action.payload as MarketCountryCode | undefined;

    if (!countryCode) {
      return;
    }

    if (marketHoursState.has(countryCode)) {
      const cachedData = marketHoursState.get(countryCode);
      observer.next(getNonWorkingDaysAndMarketTimeCompleted(cachedData));
      return;
    }

    GlobalService.fundamentalService.getNonWorkingDaysAndMarketTime(countryCode)
      .then(response => {
        const success = processResponse(
          callName,
          {},
          response,
          observer,
          getNonWorkingDaysAndMarketTimeCompleted,
          getNonWorkingDaysAndMarketTimeFailed,
        );

        if (success && response.data) {
          marketHoursState.set(response.data, countryCode);
        }
      })
      .catch(error => processErrorInResponse(callName, error, action, observer, getNonWorkingDaysAndMarketTimeFailed));
  })),
);

const processInitialNonWorkingDaysAndMarketTimeResponse = (
  response: ApiResponse<NonWorkingDaysByCountryResponse>,
  callName: string,
  countries: string[],
  observer: Observer<any>,
): void => {
  const countryResponseByCode = response.data ?? {};
  const defaultResponse = countryResponseByCode[MarketCountryCode.UnitedStates]
    ?? Object.values(countryResponseByCode)[0]
    ?? DEFAULT_NON_WORKING_DAYS_RESPONSE;

  processResponse(
    callName,
    { countries },
    response,
    observer,
    getNonWorkingDaysAndMarketTimeCompleted,
    getNonWorkingDaysAndMarketTimeFailed,
    false,
    undefined,
    defaultResponse,
    undefined,
    undefined,
    undefined,
    undefined,
    (data: NonWorkingDaysByCountryResponse) => {
      Object.entries(data).forEach(([ countryCode, countryResponse ]) => {
        marketHoursState.set(countryResponse, countryCode);
      });
    },
  );
};

const getInitialNonWorkingDaysAndMarketTimeEpic = (action$: Observable<any>) => action$.pipe(
  ofType(servicesReady.type),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    const callName = 'fundamental/getInitialNonWorkingDaysAndMarketTimeEpic';
    const countries = Object.values(MarketCountryCode);

    GlobalService.fundamentalService.getNonWorkingDaysAndMarketTimeMultiple(countries)
      .then(
        (response: ApiResponse<NonWorkingDaysByCountryResponse>) => processInitialNonWorkingDaysAndMarketTimeResponse(
          response,
          callName,
          countries,
          observer,
        ),
      )
      .catch(error => processErrorInResponse(
        callName,
        error,
        { payload: countries },
        observer,
        getNonWorkingDaysAndMarketTimeFailed,
      ));
  })),
);



function getSymbols(action: PayloadAction<string | { symbol: string }>) {
  let symbols: string = '';

  if (action.type === getCompany.type) symbols = action.payload as string;
  if (action.type === getCompanyLogoAndName.type) symbols = action.payload as string;
  if (action.type === loadStock.type) symbols = (action.payload as { symbol: string }).symbol;

  return symbols;
}

/**
 * Returns a list of symbols.
 * If `filterByCacheCallName` is given - filter symbols by it if any in
 * and update its cache if `isPendingValue` is set to `true` or `false`.
 * @param action Has a payload of a string - examples: 'AAPl', 'AAPL,TSLA', 'FB,TSLA,AAPL,AMD' etc.
 * @param filterByCacheCallName
 * @param newPendingValue
 */

export default combineEpics(
  searchStocksEpic,
  searchStockByParamsEpic,
  getPreviousEarningsEpic,
  searchEarningsEpic,
  searchStockResetEpic,
  searchKeyStatsEpic,
  getCompanyEpic,
  getCompanyLogoAndNameEpic,
  triggerGetCompanyLogoAndNameEpic,
  getPopularStocksEpic,
  getNewStocksEpic,
  getDividendPayersEpic,
  getHighGrowthCompaniesEpic,
  getMarketIndexesEpic,
  getSectorsEpic,
  getSectorDetailEpic,
  getETFsEpic,
  getETFDetailsEpic,
  getStatementsEpic,
  getStatementsCashFlowEpic,
  getStatementsBalanceSheetEpic,
  getStatementsIncomeEpic,
  getWeek52HighLowEpic,
  getWeek52HighLowTopListEpic,
  getAllRatiosEpic,
  getRatiosValuationEpic,
  getEpicMoversEpic,
  getMarketCapsEpic,
  getRatiosProfitabilityEpic,
  getRatiosLiquidityAndSolvencyEpic,
  getGappersEpic,
  getAvailableIndexesEpic,
  getNonWorkingDaysAndMarketTimeEpic,
  getInitialNonWorkingDaysAndMarketTimeEpic,
);
